import { Suspense, useState } from "react";
import { useSearch } from "@tanstack/react-router";
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer";
import { Avatar } from "@/components/catalyst/avatar";
import { Badge } from "@/components/catalyst/badge";
import { Spinner } from "@/lib/Components/Layout/Loaders/Spinner";
import { getInitials } from "@/lib/Utils/getInitials";
import {
  ProDriverLicenseType,
  TaxRegistration,
  WorkOrderAssignmentStatus,
} from "@/gql/graphql";
import { StarIcon } from "@heroicons/react/20/solid";
import {
  DocumentTextIcon,
  StarIcon as StarOutlineIcon,
  TruckIcon,
} from "@heroicons/react/24/outline";
import { useTranslations } from "use-intl";
import { useProDriverDrawer } from "@/app/ProDrivers/Drivers/Components/Drawer/useProDriverDrawer";
import { ProDriverDetailCards } from "@/app/ProDrivers/Drivers/Components/ProDriverDetailCards";
import { AssignmentsTable } from "@/app/ProDrivers/Assignments/Components/AssignmentsTable";
import { ProDriverReviewForm } from "@/app/ProDrivers/WorkOrders/Components/Dialogs/ProDriverReviewDialog";
import { useCloseSearchDrawer } from "@/lib/Hooks/useCloseSearchDrawer";

const FROM = "/$tenant/$orgId/supplier/{-$supplierId}";

type TabId = "details" | "assignments" | "reviews";

export function ProDriverDrawer() {
  const search = useSearch({ from: FROM });

  const isOpen = !!search.proDriverId;

  const handleClose = useCloseSearchDrawer("proDriverId");

  return (
    <Drawer open={isOpen} onClose={handleClose} className="relative">
      <DrawerOverlay />
      <DrawerContent showCloseButton onClose={handleClose}>
        {isOpen ? (
          <div className="flex h-full flex-col overflow-hidden">
            <Suspense
              fallback={
                <div className="flex h-full items-center justify-center">
                  <Spinner />
                </div>
              }
            >
              <ProDriverDrawerContent />
            </Suspense>
          </div>
        ) : null}
      </DrawerContent>
    </Drawer>
  );
}

function ProDriverDrawerContent() {
  const t = useTranslations("proDriver.drawer");
  const tLicense = useTranslations("proDriver.licenseType");
  const driver = useProDriverDrawer();
  const [activeTab, setActiveTab] = useState<TabId>("details");

  if (!driver) {
    return null;
  }

  const isHeavy = driver.license_type === ProDriverLicenseType.Heavy;

  return (
    <>
      <DrawerHeader>
        <div className="flex items-start gap-4 px-6 py-5">
          <Avatar
            initials={getInitials(driver.name ?? "")}
            className="ring-border h-12 w-12 shrink-0 bg-white shadow-sm ring-1"
          />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <DrawerTitle>{driver.name}</DrawerTitle>
              {driver.license_type ? (
                <Badge color={isHeavy ? "green" : "zinc"}>
                  {isHeavy
                    ? tLicense(ProDriverLicenseType.Heavy)
                    : tLicense(ProDriverLicenseType.Standard)}
                </Badge>
              ) : null}
              {driver.tax_registration !== TaxRegistration.None ? (
                <Badge color="blue">
                  {driver.tax_registration.replace("_", " ")}
                </Badge>
              ) : null}
            </div>
            <DrawerDescription>
              {driver.email}
              {driver.phone ? ` · ${driver.phone}` : null}
            </DrawerDescription>
            <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-gray-600">
              <span className="flex items-center gap-1">
                {driver.rating ? (
                  <>
                    <StarIcon className="h-4 w-4 text-yellow-400" />
                    <span className="font-medium text-gray-900">
                      {driver.rating.toFixed(1)}
                    </span>
                    {driver.numberOfReviews ? (
                      <span className="text-xs text-gray-400">
                        ({driver.numberOfReviews})
                      </span>
                    ) : null}
                  </>
                ) : (
                  <span className="text-gray-400">{t("notProvided")}</span>
                )}
              </span>
              <span>
                <span className="font-medium text-gray-900">
                  {driver.numberOfCompletedAssignments ?? 0}
                </span>{" "}
                {t("moves")}
              </span>
            </div>
          </div>
        </div>

        <div className="px-6">
          <DriverTabs activeTab={activeTab} setActiveTab={setActiveTab} />
        </div>
      </DrawerHeader>

      <DrawerBody>
        <div className="px-6 py-4">
          {activeTab === "details" ? (
            <ProDriverDetailCards proDriver={driver} />
          ) : null}
          {activeTab === "assignments" ? (
            <ProDriverAssignmentsTab proDriverId={driver.id} />
          ) : null}
          {activeTab === "reviews" ? (
            <Suspense
              fallback={
                <div className="flex justify-center py-12">
                  <Spinner />
                </div>
              }
            >
              <ProDriverReviewForm proDriverId={driver.id} />
            </Suspense>
          ) : null}
        </div>
      </DrawerBody>
    </>
  );
}

function DriverTabs({
  activeTab,
  setActiveTab,
}: {
  activeTab: TabId;
  setActiveTab: (tab: TabId) => void;
}) {
  const t = useTranslations("proDriver.drawer");

  const tabs = [
    { id: "details" as const, name: t("tabs.details"), Icon: DocumentTextIcon },
    {
      id: "assignments" as const,
      name: t("tabs.assignments"),
      Icon: TruckIcon,
    },
    { id: "reviews" as const, name: t("tabs.reviews"), Icon: StarOutlineIcon },
  ];

  return (
    <div className="flex gap-1">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          onClick={() => setActiveTab(tab.id)}
          className={`group relative flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-all ${
            activeTab === tab.id
              ? "border-indigo-500 text-indigo-600"
              : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
          }`}
        >
          <tab.Icon
            className={`h-5 w-5 shrink-0 ${
              activeTab === tab.id
                ? "text-indigo-600"
                : "text-gray-400 group-hover:text-gray-500"
            }`}
          />
          <span>{tab.name}</span>
        </button>
      ))}
    </div>
  );
}

function ProDriverAssignmentsTab({ proDriverId }: { proDriverId: string }) {
  const [status, setStatus] = useState<WorkOrderAssignmentStatus[]>([]);
  const [filterSupplierId, setFilterSupplierId] = useState<string | null>(null);

  return (
    <AssignmentsTable
      proDriverId={proDriverId}
      status={status}
      setStatus={setStatus}
      filterSupplierId={filterSupplierId}
      setFilterSupplierId={setFilterSupplierId}
    />
  );
}
